import type { AppProps } from 'next/app';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import Head from 'next/head';
import getConfig from 'next/config';

import Toast from '@/components/Toast';
import ErrorBoundary from '@/components/ErrorBoundary';
import BackToTop from '@cosmos/back-to-top';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import theme from '@/utils/mui/theme';
import createEmotionCache from '@/utils/mui/createEmotionCache';
import NewRelicSnippet from '@/components/NewRelic';

import store from '@/store/store';
import { fetchServiceCategories } from '@/store/reducers/Campaign/campaignSlice';
import { getMerchantInfo } from '@/store/reducers/Auth/authSlice';

import '@/styles/globals.scss';
import 'tailwindcss/tailwind.css';
import { hotjar } from '@/utils/helpers/hotjar';
import ImageSlider from '@/components/ImageSlider/Slider';

const { publicRuntimeConfig } = getConfig();

const clientSideEmotionCache = createEmotionCache();

function MyApp({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(() => new QueryClient());
  const emotionCache = clientSideEmotionCache;

  useEffect(() => {
    if (window && window.location.pathname.includes('merchant')) {
      store.dispatch(fetchServiceCategories());
      store.dispatch(getMerchantInfo());
      hotjar(
        window,
        document,
        'https://static.hotjar.com/c/hotjar-',
        '.js?sv='
      );
    }
  }, []);

  return (
    <>
      <Head>
        <title>Çözüm Ortağı</title>
        {publicRuntimeConfig.ENV.PLATFORM === 'prod' && <NewRelicSnippet />}
      </Head>
      <ErrorBoundary>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <CacheProvider value={emotionCache}>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <Toast />
                  <ImageSlider />
                  <Component {...pageProps} />
                </ThemeProvider>
              </CacheProvider>
            </Hydrate>
          </QueryClientProvider>
        </Provider>
      </ErrorBoundary>
      <BackToTop showUnder={300} zIndex={999} />
    </>
  );
}

export default MyApp;
